import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  CardFooter,
  FieldWithLabel,
  Input,
  Paragraph,
  NumberInput,
} from '@app/shared/ui/atoms/Common';
import { useCreateWithdrawalMutation } from '@app/store/slices/withdrawal/withdrawalApi';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import notification from '@app/shared/utils/notification';
import { generateReferralLink, telegramVibrate } from '@app/shared/utils/common';
import { useBalance } from '@app/shared/hooks/useBalance';
import { InviteFriendsModal } from '@app/shared/ui/organisms/Home';
import { useGetCurrentUserQuery } from '@app/store/slices/users/usersApi';
import { WITHDRAWAL_COMMISSION } from '@app/shared/constants/common';

interface WithdrawalFormValues {
  amount: string;
  address: string;
}

export function WithdrawalCreate() {
  const { t } = useTranslation();
  const [isInviteFriendsModalOpen, setIsInviteFriendsModalOpen] = useState(false);
  const { tonBalance } = useBalance();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<WithdrawalFormValues>({ defaultValues: { amount: '50' } });

  const { data: user } = useGetCurrentUserQuery();
  const [createWithdrawal, { isLoading: isWithdrawalCreating }] = useCreateWithdrawalMutation();
  const { handleFetchError } = useApiErrorHandler();

  const onSubmit = async (data: WithdrawalFormValues) => {
    telegramVibrate();

    try {
      await createWithdrawal({
        address: data.address.trim(),
        requestedAmount: data.amount.trim(),
      }).unwrap();
      const amount = Number(data.amount);
      const receivedAmount = amount * 0.8;

      notification.success(
        t('withdrawal.submit_success_message', { amount, received: receivedAmount })
      );
      reset();
    } catch (err: unknown) {
      const error = err as FetchBaseQueryError;

      if (
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data &&
        typeof error.data.message === 'string' &&
        error.data.message.includes('NOT_ENOUGH_REFERRALS')
      ) {
        setIsInviteFriendsModalOpen(true);
      } else {
        handleFetchError(error);
      }
    }
  };

  const referralLink = user ? generateReferralLink(user.id) : null;

  return (
    <div className="mt-5 px-3">
      <Card className="rounded-[10px] border-none !shadow-blue">
        <CardContent className="flex flex-col justify-center gap-4 pt-5">
          <CardTitle className="text-md rounded-md bg-accent py-5 text-center text-foreground">
            {t('withdrawal.title')}
          </CardTitle>

          <FieldWithLabel
            className="font-thin"
            label={`${t('withdrawal.amount_label')}:`}
            hasErrorField
            error={errors.amount?.message}
          >
            <NumberInput
              className="focus:border-black border-2 border-transparent px-2 shadow-none"
              min={50}
              step={1}
              placeholder={t('withdrawal.amount_placeholder')}
              {...register('amount', {
                setValueAs: (v: string) => v.trim(),
                required: t('withdrawal.errors.amount_required'),
                validate: {
                  isNumber: (value) =>
                    !isNaN(Number(value)) || t('withdrawal.errors.amount_invalid'),
                  precision: (value) =>
                    /^\d+(\.\d{1,8})?$/.test(value) || t('withdrawal.errors.amount_precision'),
                  notExceedBalance: (value) =>
                    Number(value) <= tonBalance || t('withdrawal.errors.not_enough_balance'),
                },
                min: {
                  value: 50,
                  message: t('withdrawal.errors.amount_min'),
                },
                max: {
                  value: 300,
                  message: t('withdrawal.errors.amount_max'),
                },
              })}
            />
          </FieldWithLabel>

          <FieldWithLabel
            className="font-thin"
            label={`${t('withdrawal.address_label')}:`}
            hasErrorField
            error={errors.address?.message}
          >
            <Input
              className="focus:border-black border-2 border-transparent px-2 shadow-none"
              placeholder={t('withdrawal.address_placeholder')}
              {...register('address', {
                setValueAs: (v: string) => v.trim(),
                required: t('withdrawal.errors.address_required'),
                validate: {
                  noSpaces: (value) => !/\s/.test(value) || t('withdrawal.errors.address_spaces'),
                },
              })}
            />
          </FieldWithLabel>

          <CardDescription className="text-black text-center text-sm">
            {t('withdrawal.form_info', { value: WITHDRAWAL_COMMISSION })}
          </CardDescription>

          <Button
            className="text-md my-2 rounded-md p-0 px-4 hover:bg-[#6ba6dd] disabled:opacity-60"
            disabled={isWithdrawalCreating}
            shadow="cyan"
            onClick={handleSubmit(onSubmit)}
          >
            {t('withdrawal.submit_button')}
          </Button>

          <Paragraph className="px-4 text-center text-xs font-thin">
            {t('withdrawal.note')}
          </Paragraph>
        </CardContent>
      </Card>
      <CardFooter className="m-5 flex justify-center text-center text-[10px]">
        {t('withdrawal.footer_warning')}
      </CardFooter>

      {referralLink && (
        <InviteFriendsModal
          referralLink={referralLink}
          isOpen={isInviteFriendsModalOpen}
          onOpenChange={setIsInviteFriendsModalOpen}
          modalTextTranslationKey="withdrawal.errors.not_enough_refs_ferm"
        />
      )}
    </div>
  );
}
